/**
 * 앱 배포 — 관리 콘솔 (2026-09-14 · 계약 `docs/api/app-releases-contract-2026-09-14.md` 「안 A」).
 *
 * 2026-09-14 결정은 「안 B = 운영 스크립트 등록, 필요하면 콘솔」이었고, 이 화면이 그 「콘솔」이다.
 * 서버 · 검사 · 감사는 전부 chat-server 것을 그대로 쓴다(BFF 는 나르기만) — 스크립트와 화면이 같은 API 를 부른다.
 *
 * 목록 공통 규격(2026-09-09): 검색 · 필터 · 등록은 목록 위 / 좌 분류 = 플랫폼 / 작업은 줄 끝 단추 / 줄을 누르면 상세 팝업.
 *
 * 상태 전이(서버 `canTransition`): PENDING→DRAFT|PUBLISHED(확정) · DRAFT→PUBLISHED|REVOKED · PUBLISHED→REVOKED · REVOKED 는 끝.
 *   → 줄 끝 단추도 그대로: 초안=게시·회수 / 게시=회수 / 대기=없음(같은 번호로 다시 등록하면 덮어쓴다) / 회수=없음.
 *
 * 아래 「기관별 배포 방식」 — MDM 으로 앱을 까는 회사는 QR 대신 안내만 보인다(내려받기 403). 기본 QR.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Smartphone, X } from '../../../icons';
import {
  listAppReleases, publishAppRelease, revokeAppRelease, getTenantAppDistribution, setTenantAppDistribution,
  type AdminAppRelease, type AppReleaseStatus, type AppReleasePlatform, type AppDistributionMode, type AppReleaseTransitionResult,
} from '../../../api/appReleases';
import { listSystemTenants, type SystemTenant } from '../../../api/systemUsers';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';
import { AppReleaseRegisterModal, errText } from './AppReleaseRegisterModal';
import { formatBytes } from './appReleaseIntake';
import './ServiceModules.css';
import './UserWorkspace.css';
import './AppRelease.css';

const STATUS_LABEL: Record<AppReleaseStatus, string> = {
  PENDING: '업로드 대기', DRAFT: '초안', PUBLISHED: '게시', REVOKED: '회수',
};
const STATUS_CLASS: Record<AppReleaseStatus, string> = {
  PENDING: 'ar-badge--pending', DRAFT: 'ar-badge--draft', PUBLISHED: 'ar-badge--published', REVOKED: 'ar-badge--revoked',
};
const FILTERS: Array<{ key: 'all' | AppReleaseStatus; label: string }> = [
  { key: 'all', label: '전체' }, { key: 'PUBLISHED', label: '게시' }, { key: 'DRAFT', label: '초안' },
  { key: 'PENDING', label: '대기' }, { key: 'REVOKED', label: '회수' },
];

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function AppReleaseSection() {
  const [list, setList] = useState<AdminAppRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [platform, setPlatform] = useState<AppReleasePlatform>('ANDROID');
  const [filter, setFilter] = useState<'all' | AppReleaseStatus>('all');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [detail, setDetail] = useState<AdminAppRelease | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setList(await listAppReleases()); }
    catch (e: any) { setError(errText(e, '앱 목록 불러오기 실패')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const inPlatform = useMemo(() => list.filter((r) => r.platform === platform), [list, platform]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return inPlatform.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (!needle) return true;
      return `${r.versionName} ${r.versionCode} ${r.buildNumber ?? ''} ${r.fileName}`.toLowerCase().includes(needle);
    });
  }, [inPlatform, filter, q]);

  function afterTransition(verb: string, r: AppReleaseTransitionResult) {
    const auto = r.autoRevoked?.length ? ` · 오래된 게시본 ${r.autoRevoked.map((a) => a.versionCode).join(', ')} 은 자동 회수됐습니다(최신 3개 보존)` : '';
    setNotice(`${r.release.versionName} (versionCode ${r.release.versionCode}) ${verb}${auto}`);
  }

  async function publish(r: AdminAppRelease) {
    if (!window.confirm(`${r.versionName} (versionCode ${r.versionCode}) 을 게시할까요? 사원의 다운로드 화면에 바로 보입니다.`)) return;
    setBusyId(r.id); setError(null);
    try { afterTransition('게시됨', await publishAppRelease(r.id)); await reload(); }
    catch (e: any) { setError(errText(e, '게시 실패')); }
    finally { setBusyId(null); }
  }

  async function revoke(r: AdminAppRelease) {
    if (!window.confirm(`${r.versionName} (versionCode ${r.versionCode}) 을 회수할까요? 되살릴 수 없고, 같은 번호는 다시 쓸 수 없습니다.`)) return;
    setBusyId(r.id); setError(null);
    try { afterTransition('회수됨', await revokeAppRelease(r.id)); await reload(); }
    catch (e: any) { setError(errText(e, '회수 실패')); }
    finally { setBusyId(null); }
  }

  const publishedCount = list.filter((r) => r.status === 'PUBLISHED').length;
  const countOf = (key: 'all' | AppReleaseStatus) => (key === 'all' ? inPlatform : inPlatform.filter((r) => r.status === key)).length;

  return (
    <>
      <ConsoleListScreen
        title="앱 배포"
        kpi={`${list.length}개 · 게시 ${publishedCount}`}
        desc="사원이 설정 › 앱 다운로드(QR) 에서 받는 설치 파일. 등록은 시스템 관리자만 하며 인스턴스 전체에 같은 앱이 배포됩니다."
        search={{ value: q, onChange: setQ, placeholder: '버전 · 빌드 번호로 찾기' }}
        filters={{ active: filter, onChange: (k) => setFilter(k as typeof filter), items: FILTERS.map((f) => ({ key: f.key, label: f.label, count: countOf(f.key) })) }}
        add={{ label: '앱 등록', onClick: () => setShowRegister(true), disabled: platform !== 'ANDROID' }}
        side={{
          head: '플랫폼', active: platform, onChange: (k) => setPlatform(k as AppReleasePlatform),
          items: [
            { key: 'ANDROID', label: 'Android', count: list.filter((r) => r.platform === 'ANDROID').length, icon: <Smartphone size={14} /> },
            { key: 'IOS', label: 'iOS', count: list.filter((r) => r.platform === 'IOS').length, icon: <Smartphone size={14} /> },
          ],
        }}
        notice={
          <>
            {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
            {notice && (
              <div className="ar-note ar-note--info" role="status">
                <button type="button" className="ar-note__x" aria-label="닫기" onClick={() => setNotice(null)}>×</button>
                {notice}
              </div>
            )}
            {platform === 'IOS' && (
              <div className="admin-console__alert" role="status">iOS 는 아직 등록할 수 없습니다 — 서버가 받지 않습니다(인증서 결정 뒤 별도 계약).</div>
            )}
          </>
        }
      >
        {loading ? (
          <div className="cl-empty">불러오는 중…</div>
        ) : (
          <ConsoleListTable<AdminAppRelease>
            rows={shown}
            rowKey={(r) => r.id}
            onRowClick={(r) => setDetail(r)}
            columns={[
              {
                key: 'version', header: '버전',
                render: (r) => (
                  <span className="cl-name">
                    <span className="cl-name__t">{r.versionName} <span className="ar-muted">(versionCode {r.versionCode})</span></span>
                    <span className="cl-name__s">{r.fileName}{r.buildNumber ? ` · ${r.buildNumber}` : ''}</span>
                  </span>
                ),
              },
              { key: 'status', header: '상태', render: (r) => <span className={`cl-badge ${STATUS_CLASS[r.status]}`}>{STATUS_LABEL[r.status]}</span> },
              { key: 'size', header: '크기', render: (r) => formatBytes(r.sizeBytes) },
              { key: 'min', header: '최소 지원', render: (r) => (r.minSupportedVersionCode == null ? <span className="ar-muted">-</span> : r.minSupportedVersionCode) },
              { key: 'published', header: '게시일', render: (r) => (r.status === 'REVOKED' ? <span className="ar-muted">회수 {fmtDate(r.revokedAt)}</span> : fmtDate(r.publishedAt)) },
              { key: 'by', header: '등록', render: (r) => <span className="ar-muted">{r.createdBy?.name ?? r.createdBy?.id ?? '-'} · {fmtDate(r.createdAt)}</span> },
            ]}
            rowActions={(r) => {
              const busy = busyId === r.id;
              if (r.status === 'DRAFT') {
                return (
                  <>
                    <button type="button" className="cl-rowbtn" disabled={busy} onClick={() => void publish(r)}>게시</button>
                    <button type="button" className="cl-rowbtn cl-rowbtn--danger" disabled={busy} onClick={() => void revoke(r)}>회수</button>
                  </>
                );
              }
              if (r.status === 'PUBLISHED') {
                return <button type="button" className="cl-rowbtn cl-rowbtn--danger" disabled={busy} onClick={() => void revoke(r)}>회수</button>;
              }
              if (r.status === 'PENDING') return <span className="ar-muted">파일 업로드 전 — 같은 번호로 다시 등록하면 덮어씁니다</span>;
              return <span className="ar-muted">-</span>;
            }}
            empty={<>{platform === 'ANDROID' ? 'Android' : 'iOS'} 앱이 아직 없습니다.<span className="cl-empty__sub">「앱 등록」으로 빌드 산출물(APK · app-meta.json)을 올리세요.</span></>}
          />
        )}

        <DistributionPanel />
      </ConsoleListScreen>

      {showRegister && (
        <AppReleaseRegisterModal
          onClose={() => setShowRegister(false)}
          onDone={(r) => { setShowRegister(false); afterTransition(r.release.status === 'PUBLISHED' ? '등록 · 게시됨' : '등록됨(초안)', r); void reload(); }}
        />
      )}
      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

/** 줄을 누르면 — 지문 · sha256 · 변경 내용처럼 표에 다 못 싣는 것. 읽기 전용. */
function DetailModal({ row, onClose }: { row: AdminAppRelease; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="svc-modal__backdrop" role="presentation" onClick={onClose}>
      <div className="svc-modal ar-modal" role="dialog" aria-modal="true" aria-labelledby="ar-detail-title" onClick={(e) => e.stopPropagation()}>
        <div className="svc-modal__head">
          <h3 id="ar-detail-title">{row.versionName} <span className="ar-muted">(versionCode {row.versionCode})</span></h3>
          <button type="button" className="svc-modal__close" onClick={onClose} aria-label="닫기"><X size={16} /></button>
        </div>
        <div className="svc-modal__body">
          <dl className="ar-kv">
            <dt>상태</dt><dd><span className={`cl-badge ${STATUS_CLASS[row.status]}`}>{STATUS_LABEL[row.status]}</span></dd>
            <dt>플랫폼</dt><dd>{row.platform}</dd>
            <dt>package</dt><dd className="ar-mono">{row.packageName}</dd>
            <dt>파일</dt><dd>{row.fileName} · {formatBytes(row.sizeBytes)} <span className="ar-muted">({row.sizeBytes.toLocaleString()} 바이트)</span></dd>
            <dt>sha256</dt><dd className="ar-mono">{row.sha256}</dd>
            <dt>서명 지문</dt><dd className="ar-mono">{row.signingCertSha256 ?? '-'}</dd>
            <dt>빌드 번호</dt><dd>{row.buildNumber ?? '-'}</dd>
            <dt>최소 지원</dt><dd>{row.minSupportedVersionCode ?? '-'}</dd>
            <dt>게시일</dt><dd>{fmtDate(row.publishedAt)}</dd>
            <dt>회수일</dt><dd>{fmtDate(row.revokedAt)}</dd>
            <dt>등록</dt><dd>{row.createdBy?.name ?? row.createdBy?.id ?? '-'} · {fmtDate(row.createdAt)}</dd>
            <dt>변경 내용</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{row.releaseNotes || <span className="ar-muted">(없음)</span>}</dd>
          </dl>
        </div>
        <div className="svc-modal__foot">
          <span className="spacer" />
          <button type="button" className="svc-mbtn" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

interface DistRow { tenant: SystemTenant; mode: AppDistributionMode; mdmNote: string; saved: { mode: AppDistributionMode; mdmNote: string } | null; err: string | null }

/**
 * 기관별 배포 방식 — 회사마다 QR / MDM. 목록은 회사 목록(`/system/tenants`)에서, 값은 회사마다 한 번씩 읽는다
 * (회사 수가 적어 왕복이 문제되지 않는다 · 일괄 조회 라우트를 새로 만들지 않는다 — main 은 배포 기준선).
 */
function DistributionPanel() {
  const [rows, setRows] = useState<DistRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const tenants = await listSystemTenants();
        // ★ 순서대로 읽는다 — 회사 수만큼 한꺼번에 쏘면 사용자별 60/분 버킷(429)에 걸릴 수 있다(리뷰-화면 #1332).
        const dists: Array<{ mode: AppDistributionMode; mdmNote: string; err: string | null }> = [];
        for (const t of tenants) {
          if (!alive) return;
          try { const d = await getTenantAppDistribution(t.id); dists.push({ mode: d.mode, mdmNote: d.mdmNote ?? '', err: null }); }
          catch (e: any) { dists.push({ mode: 'QR', mdmNote: '', err: errText(e, '배포 방식을 읽지 못했습니다') }); }
        }
        if (!alive) return;
        setRows(tenants.map((t, i) => ({
          tenant: t, mode: dists[i]!.mode, mdmNote: dists[i]!.mdmNote,
          saved: dists[i]!.err ? null : { mode: dists[i]!.mode, mdmNote: dists[i]!.mdmNote }, err: dists[i]!.err,
        })));
      } catch (e: any) { if (alive) setError(errText(e, '회사 목록을 읽지 못했습니다')); }
    })();
    return () => { alive = false; };
  }, []);

  function patch(id: string, p: Partial<DistRow>) {
    setRows((arr) => arr.map((r) => (r.tenant.id === id ? { ...r, ...p } : r)));
  }

  async function save(r: DistRow) {
    setBusy(r.tenant.id); patch(r.tenant.id, { err: null });
    try {
      const d = await setTenantAppDistribution(r.tenant.id, { mode: r.mode, mdmNote: r.mode === 'MDM' ? (r.mdmNote.trim() || null) : null });
      patch(r.tenant.id, { mode: d.mode, mdmNote: d.mdmNote ?? '', saved: { mode: d.mode, mdmNote: d.mdmNote ?? '' } });
    } catch (e: any) { patch(r.tenant.id, { err: errText(e, '저장 실패') }); }
    finally { setBusy(null); }
  }

  return (
    <section className="ar-dist" aria-labelledby="ar-dist-title">
      <div className="ar-dist__head">
        <h3 id="ar-dist-title">기관별 배포 방식</h3>
        <span className="ar-dist__desc">MDM 으로 앱을 까는 회사는 QR 카드 대신 안내 문구만 보이고, 내려받기는 막힙니다(403). 기본은 QR.</span>
      </div>
      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
      {!error && rows.length === 0 && <div className="ar-muted">회사가 없거나 불러오는 중입니다.</div>}
      {rows.length > 0 && (
        <table className="cl-table">
          <thead><tr><th>회사</th><th>배포 방식</th><th>MDM 안내 문구</th><th className="cl-table__act">작업</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const dirty = !r.saved || r.saved.mode !== r.mode || (r.mode === 'MDM' && r.saved.mdmNote !== r.mdmNote);
              return (
                <tr key={r.tenant.id}>
                  <td><span className="cl-name"><span className="cl-name__t">{r.tenant.name}</span><span className="cl-name__s">{r.tenant.code}</span></span></td>
                  <td>
                    <select aria-label={`${r.tenant.name} 배포 방식`} value={r.mode} disabled={busy === r.tenant.id} onChange={(e) => patch(r.tenant.id, { mode: e.target.value as AppDistributionMode })}>
                      <option value="QR">QR (사원이 직접 내려받음)</option>
                      <option value="MDM">MDM (회사가 배포)</option>
                    </select>
                  </td>
                  <td>
                    {r.mode === 'MDM' ? (
                      <input type="text" aria-label={`${r.tenant.name} MDM 안내 문구`} value={r.mdmNote} maxLength={500} placeholder="예: 회사 MDM 포털에서 설치하세요" disabled={busy === r.tenant.id} onChange={(e) => patch(r.tenant.id, { mdmNote: e.target.value })} />
                    ) : <span className="ar-muted">-</span>}
                    {r.err && <div className="ar-muted" role="alert" style={{ color: 'var(--color-danger)' }}>{r.err}</div>}
                  </td>
                  <td className="cl-table__act">
                    <span className="cl-table__acts">
                      <button type="button" className="cl-rowbtn" disabled={!dirty || busy === r.tenant.id} onClick={() => void save(r)}>저장</button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
